import { useState } from "react";

import * as privateApi from "../services/privateTalentPulseService";

const getMessage = (err) =>
  err?.response?.data?.message ||
  err?.message ||
  "Unable to process Talent Pulse request.";

const getRecords = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;

  return [];
};

export const usePrivateTalentPulse = (readOnly = false) => {
  const [pulses, setPulses] = useState([]);
  const [dataset, setDataset] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingDataset, setLoadingDataset] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState(null);
  const [datasetError, setDatasetError] = useState(null);

  const getPulses = async (talentId, talentTag) => {
    try {
      setLoading(true);
      setError(null);
      setPulses([]);

      let response;

      if (readOnly) {
        if (!talentTag) {
          throw new Error("Talent Tag is required for shared Pulse.");
        }

        response = await privateApi.getSharedTalentPulse(talentTag);
      } else {
        if (!talentId) {
          throw new Error("Talent ID is required.");
        }

        response = await privateApi.getPrivateTalentPulse(talentId);
      }

      const results = getRecords(response);

      setPulses(results);

      return results;
    } catch (err) {
      setError(getMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getDataset = async (sport) => {
    if (readOnly) return null;

    try {
      setLoadingDataset(true);
      setDatasetError(null);
      setDataset(null);

      const response = await privateApi.getPrivateTalentPulseDataset(sport);

      const result = response.data?.data || response.data;

      setDataset(result);
      return result;
    } catch (err) {
      setDatasetError(getMessage(err));
      throw err;
    } finally {
      setLoadingDataset(false);
    }
  };

  const savePulse = async (talentId, talentSportId, answers) => {
    if (readOnly) {
      throw new Error("Cannot modify a shared Talent Pulse.");
    }

    try {
      setSaving(true);
      setError(null);

      const response = await privateApi.upsertPrivateTalentPulse(
        talentId,
        talentSportId,
        answers,
      );

      const saved = response.data?.data;

      if (!saved) {
        throw new Error("Pulse result was not returned.");
      }

      setPulses((current) => [
        ...current.filter(
          (item) => Number(item.talentSportId) !== Number(talentSportId),
        ),
        saved,
      ]);

      return saved;
    } catch (err) {
      setError(getMessage(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const removePulse = async (pulseId) => {
    if (readOnly) {
      throw new Error("Cannot delete a shared Talent Pulse.");
    }

    try {
      setSaving(true);
      setError(null);

      await privateApi.deletePrivateTalentPulse(pulseId);

      setPulses((current) =>
        current.filter((item) => Number(item.pulseId) !== Number(pulseId)),
      );

      return true;
    } catch (err) {
      setError(getMessage(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return {
    pulses,
    dataset,
    loading,
    loadingDataset,
    saving,
    error,
    datasetError,
    getPulses,
    getDataset,
    savePulse,
    removePulse,
  };
};
